import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { apiFetch } from "../../core/utils/api";
import { useEdit } from "./EditContext";
import {
  getHasActiveOperations,
  getAssistantMessageIdsWithActiveOperations,
  getNewlyCompletedOperationReloadTargets,
  dedupeReloadTargets,
} from "./aiPollingUtils";

/**
 * AIContext
 * 
 * Provides AI chat functionality for the website editor.
 * Manages chat history, messages, operations, and usage tracking.
 */
export const AIContext = createContext();

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error("useAI must be used within an AIProvider");
  }
  return context;
};

export const AIProvider = ({ children }) => {
  const { refreshElement } = useEdit();

  // State
  const [chats, setChats] = useState([]);
  // Initialize from localStorage if available
  const [currentChatId, setCurrentChatId] = useState(() => {
    return localStorage.getItem('ai_current_chat_id') || null;
  });
  const [currentChat, setCurrentChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState(null);
  
  // Ref for polling interval to clear it on unmount
  const pollingIntervalRef = useRef(null);

  // Timeout handling for long-running operations
  const pollStartTimeRef = useRef(null);
  const tickCountRef = useRef(0);
  const OPERATION_TIMEOUT_MS = 60000; // 60 seconds timeout
  const currentChatRef = useRef(null);
  const completedOpsByMessageIdRef = useRef(new Map()); // messageId -> Set(operationId)

  const resolveWebsiteId = useCallback((websiteId) => {
    if (!websiteId) return null;
    if (typeof websiteId === "object") {
      return (
        websiteId.id ||
        websiteId.website_id ||
        websiteId.website?.id ||
        websiteId.value ||
        null
      );
    }
    return websiteId;
  }, []);

  const deriveChatTitleFromUserMessage = useCallback((message) => {
    if (!message || typeof message !== "string") return null;
    const normalized = message.replace(/\s+/g, " ").trim();
    if (!normalized) return null;
    const normalizedLower = normalized.toLowerCase();
    const maxLength = 10;
    if (normalizedLower.length <= maxLength) {
      return normalizedLower.charAt(0).toUpperCase() + normalizedLower.slice(1);
    }
    // Take the first words from the message; cap to 10 chars total with ellipsis.
    const words = normalizedLower.split(" ");
    let base = "";
    for (const word of words) {
      const candidate = base ? `${base} ${word}` : word;
      if (candidate.length > maxLength) {
        break;
      }
      base = candidate;
    }
    if (!base) {
      base = normalizedLower.slice(0, maxLength - 3).trimEnd();
    }
    const truncated = base.length > maxLength - 3 ? base.slice(0, maxLength - 3).trimEnd() : base;
    const titled = truncated ? truncated.charAt(0).toUpperCase() + truncated.slice(1) : truncated;
    return `${titled}...`;
  }, []);

  const isDefaultChatTitle = useCallback((title) => {
    const t = String(title || "").trim().toLowerCase();
    return !t || t === "new chat";
  }, []);

  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  const hasActiveOperations = useMemo(() => getHasActiveOperations(currentChat), [currentChat]);

  // Reset seen completion transitions when switching chats
  useEffect(() => {
    completedOpsByMessageIdRef.current = new Map();
    tickCountRef.current = 0;
    pollStartTimeRef.current = null;
  }, [currentChatId]);

  // Persist currentChatId
  useEffect(() => {
    if (currentChatId) {
      localStorage.setItem('ai_current_chat_id', currentChatId);
    } else {
      localStorage.removeItem('ai_current_chat_id');
    }
  }, [currentChatId]);

  // Fetch chats for a website
  const fetchChats = useCallback(async (websiteId, { includeArchived = false } = {}) => {
    const resolvedWebsiteId = resolveWebsiteId(websiteId);
    if (!resolvedWebsiteId) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        website_id: String(resolvedWebsiteId),
        archived: includeArchived ? "true" : "false",
      });
      const response = await apiFetch(`/api/ai/chats/?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        // Backend returns { count: ..., results: [...] }
        // Handle both array (legacy?) and object format
        const chatList = Array.isArray(data) ? data : (data.results || []);
        setChats(chatList);
        return chatList;
      } else {
        console.error("Failed to fetch chats");
        return [];
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [resolveWebsiteId]);

  // Fetch a specific chat
  const fetchChat = useCallback(async (chatId, silent = false) => {
    if (!chatId) return;
    
    try {
      if (!silent) setLoading(true);
      const response = await apiFetch(`/api/ai/chats/${chatId}/`);
      if (response.ok) {
        const data = await response.json();
        setCurrentChat(data);
        // Ensure ID is synced (e.g. if passed from param)
        if (chatId !== currentChatId) {
          setCurrentChatId(chatId);
        }
      } else {
        console.error("Failed to fetch chat");
        // If 404, maybe clear local storage?
        if (response.status === 404 && chatId === currentChatId) {
           setCurrentChatId(null);
           localStorage.removeItem('ai_current_chat_id');
        }
      }
    } catch (error) {
      console.error("Error fetching chat:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentChatId]);

  // Create a new chat
  const createChat = useCallback(async (websiteId, title = "New Chat") => {
    const resolvedWebsiteId = resolveWebsiteId(websiteId);
    if (!resolvedWebsiteId) return null;
    
    try {
      setLoading(true);
      const response = await apiFetch(`/api/ai/chats/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website_id: resolvedWebsiteId, title }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setChats(prev => [data, ...(Array.isArray(prev) ? prev : [])]);
        setCurrentChat(data);
        setCurrentChatId(data.id);
        return data;
      } else {
        console.error("Failed to create chat");
        return null;
      }
    } catch (error) {
      console.error("Error creating chat:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [resolveWebsiteId]);

  // Check operation status for a message (for polling / one-shot completion checks)
  const checkOperationStatus = useCallback(async (messageId) => {
    if (!messageId) return null;

    try {
      const response = await apiFetch(`/api/ai/operations/message/${messageId}/status/`);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error("Failed to check operation status");
        return null;
      }
    } catch (error) {
      console.error("Error checking operation status:", error);
      return null;
    }
  }, []);

  // Send a message
  const sendMessage = useCallback(async (chatId, message, context = {}, extraPayload = {}) => {
    if (!chatId || !message) return null;

    // If this is the first user message in a "New Chat", auto-title the chat based on the message.
    const chatBeforeSend = currentChatRef.current?.id === chatId ? currentChatRef.current : null;
    const shouldAutoTitle =
      !!chatBeforeSend &&
      isDefaultChatTitle(chatBeforeSend.title) &&
      (chatBeforeSend.messages || []).filter((m) => m.role === "user").length === 0;
    const proposedTitle = shouldAutoTitle ? deriveChatTitleFromUserMessage(message) : null;
    
    // 1. Optimistic Update
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      role: 'user',
      content: message,
      created_at: new Date().toISOString(),
      pending: true
    };

    setCurrentChat(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...(prev.messages || []), tempMessage]
      };
    });

    try {
      setSending(true);
      const response = await apiFetch(`/api/ai/chats/${chatId}/messages/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          content: message,
          context: context,
          ...(extraPayload && typeof extraPayload === "object" ? extraPayload : {}),
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const { user_message, assistant_message, followup_message, intro_message, chat_title, execution_started } = data;

        const assistantMessages = [];
        // If backend provided a separate intro message, show it first.
        if (intro_message && intro_message.id) {
          assistantMessages.push(intro_message);
        }
        if (assistant_message && assistant_message.id) {
          assistantMessages.push(assistant_message);
        }
        if (followup_message && followup_message.id) {
          assistantMessages.push(followup_message);
        }

        // Replace optimistic message with real ones
        setCurrentChat(prev => {
          if (!prev) return prev;
          // Remove temp message
          const filtered = (prev.messages || []).filter(m => m.id !== tempId);
          
          // Update title if provided
          if (chat_title) {
            return {
              ...prev,
              title: chat_title,
              messages: [...filtered, user_message, ...assistantMessages]
            };
          }
          
          return {
            ...prev,
            messages: [...filtered, user_message, ...assistantMessages]
          };
        });
        
        const effectiveTitle = proposedTitle || chat_title;

        // Update title in chats list if changed
        if (effectiveTitle) {
          setChats(prev =>
            (Array.isArray(prev) ? prev : []).map(chat =>
              chat.id === chatId ? { ...chat, title: effectiveTitle } : chat
            )
          );
        }

        // If this is the first message in a "New Chat", always persist the derived title.
        if (proposedTitle) {
          try {
            await apiFetch(`/api/ai/chats/${chatId}/`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ title: proposedTitle }),
            });
            const websiteIdForReload = chatBeforeSend?.website || currentChatRef.current?.website;
            if (websiteIdForReload) {
              fetchChats(websiteIdForReload);
            } else {
              fetchChat(chatId, true);
            }
          } catch (e) {
            // Ignore title update failures; chat still works.
          }
        }
        
        // If execution started, we rely on polling effect to update status
        // NOTE: In the declarative CONTENT engine, bucket-level operations can complete immediately.
        // In that case, `hasActiveOperations` may remain false and polling won't start, so we do a one-shot
        // status fetch + refresh using reload_targets.
        try {
          if (execution_started && assistant_message?.id) {
            const statusPayload = await checkOperationStatus(assistant_message.id);
            const ops = statusPayload?.operations;
            if (Array.isArray(ops)) {
              // Merge operations into local state
              setCurrentChat((prev) => {
                if (!prev) return prev;
                const nextMessages = (prev.messages || []).map((m) => {
                  if (m.id !== assistant_message.id) return m;
                  return { ...m, operations: ops };
                });
                return { ...prev, messages: nextMessages };
              });

              const completedSet =
                completedOpsByMessageIdRef.current.get(assistant_message.id) || new Set();
              const { newlyCompletedOperationIds, reloadTargets } =
                getNewlyCompletedOperationReloadTargets(ops, completedSet);
              if (newlyCompletedOperationIds.size > 0) {
                newlyCompletedOperationIds.forEach((id) => completedSet.add(id));
                completedOpsByMessageIdRef.current.set(assistant_message.id, completedSet);
              }

              const dedupedTargets = dedupeReloadTargets(reloadTargets);
              for (const target of dedupedTargets) {
                try {
                  await refreshElement(target.element_id, target.element_type);
                } catch (e) {
                  console.warn("[AIContext] refreshElement failed (one-shot)", e);
                }
              }
            }
          }
        } catch (e) {
          // Non-fatal: chat UX should still work even if refresh fails.
          console.warn("[AIContext] one-shot operation status fetch failed", e);
        }

        return data;
      } else {
        let payload = null;
        try {
          payload = await response.json();
        } catch (_) {
          payload = null;
        }
        const errorCode = payload?.code || payload?.error || "send_failed";
        const errorMessage = payload?.error || "Failed to send message";

        console.error("Failed to send message", { status: response.status, errorCode });
        // Revert optimistic update (message was not sent).
        setCurrentChat(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: (prev.messages || []).filter(m => m.id !== tempId)
          };
        });
        return {
          error: {
            code: errorCode,
            message: errorMessage,
            status: response.status,
            reset_date: payload?.reset_date,
          }
        };
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Revert optimistic update (message was not sent).
      setCurrentChat(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).filter(m => m.id !== tempId)
        };
      });
      return {
        error: {
          code: "network_error",
          message: "Network error. Please try again.",
        }
      };
    } finally {
      setSending(false);
    }
  }, [checkOperationStatus, deriveChatTitleFromUserMessage, fetchChat, fetchChats, isDefaultChatTitle, refreshElement]);

  const updateChat = useCallback(async (chatId, updates = {}) => {
    if (!chatId || !updates || typeof updates !== "object") return null;

    try {
      const response = await apiFetch(`/api/ai/chats/${chatId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        return { error: data?.error || "Failed to update chat", code: data?.code || "update_chat_failed" };
      }

      setChats((prev) =>
        (Array.isArray(prev) ? prev : []).map((chat) =>
          chat.id === chatId
            ? {
                ...chat,
                ...("title" in updates ? { title: data?.title ?? updates.title } : {}),
                ...("is_archived" in updates ? { is_archived: data?.is_archived ?? updates.is_archived } : {}),
                ...("chat_type" in updates ? { chat_type: data?.chat_type ?? updates.chat_type } : {}),
                ...("review_phase" in data ? { review_phase: data.review_phase } : {}),
                updated_at: data?.updated_at || chat.updated_at,
              }
            : chat
        )
      );
      setCurrentChat((prev) => {
        if (!prev || prev.id !== chatId) return prev;
        return {
          ...prev,
          ...("title" in updates ? { title: data?.title ?? updates.title } : {}),
          ...("is_archived" in updates ? { is_archived: data?.is_archived ?? updates.is_archived } : {}),
          ...("chat_type" in updates ? { chat_type: data?.chat_type ?? updates.chat_type } : {}),
          ...("review_phase" in data ? { review_phase: data.review_phase } : {}),
        };
      });
      return data;
    } catch (error) {
      console.error("Error updating chat:", error);
      return { error: "Network error updating chat", code: "network_error" };
    }
  }, []);

  const sendReviewAction = useCallback(async (chatId, action, { content = "", context = {} } = {}) => {
    if (!chatId || !action) return null;
    return sendMessage(chatId, content, context, { action });
  }, [sendMessage]);

  const startReviewChat = useCallback(async (chatId) => {
    if (!chatId) return null;

    const chatSnapshot = currentChatRef.current?.id === chatId ? currentChatRef.current : null;
    const previousChatType = chatSnapshot?.chat_type || "standard";
    const switched = previousChatType !== "site_review";

    if (switched) {
      const updateResult = await updateChat(chatId, { chat_type: "site_review" });
      if (updateResult?.error) {
        return updateResult;
      }
    }

    try {
      setSending(true);
      const response = await apiFetch(`/api/ai/chats/${chatId}/messages/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "review_start" }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (switched) {
          await updateChat(chatId, { chat_type: previousChatType });
        }
        return {
          error: {
            code: data?.code || "review_start_failed",
            message: data?.error || "Failed to start review",
            status: response.status,
          },
        };
      }

      const { assistant_message, intro_message, chat_title } = data || {};
      const assistantMessages = [];
      if (intro_message?.id) assistantMessages.push(intro_message);
      if (assistant_message?.id) assistantMessages.push(assistant_message);

      setCurrentChat((prev) => {
        if (!prev || prev.id !== chatId) return prev;
        return {
          ...prev,
          chat_type: "site_review",
          ...(assistant_message?.context?.review_phase ? { review_phase: assistant_message.context.review_phase } : {}),
          messages: [...(prev.messages || []), ...assistantMessages],
        };
      });

      setChats((prev) =>
        (Array.isArray(prev) ? prev : []).map((chat) =>
          chat.id === chatId
            ? { ...chat, chat_type: "site_review", ...(chat_title ? { title: chat_title } : {}) }
            : chat
        )
      );

      // Review mode can auto-pin website creation assets on the backend, so refresh chat detail
      // immediately to keep the visible pinned assets in sync with the prompt context.
      await fetchChat(chatId, true);

      return data;
    } catch (error) {
      console.error("Error starting review chat:", error);
      if (switched) {
        await updateChat(chatId, { chat_type: previousChatType });
      }
      return {
        error: {
          code: "network_error",
          message: "Network error. Please try again.",
        },
      };
    } finally {
      setSending(false);
    }
  }, [fetchChat, updateChat]);

  const endReviewChat = useCallback(async (chatId) => {
    if (!chatId) return null;
    try {
      setSending(true);
      const response = await apiFetch(`/api/ai/chats/${chatId}/messages/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "review_end" }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          error: {
            code: data?.code || "review_end_failed",
            message: data?.error || "Failed to end review",
            status: response.status,
          },
        };
      }

      const assistantMessage = data?.assistant_message?.id ? [data.assistant_message] : [];

      setCurrentChat((prev) => {
        if (!prev || prev.id !== chatId) return prev;
        return {
          ...prev,
          chat_type: "standard",
          review_phase: "consultation",
          messages: [...(prev.messages || []), ...assistantMessage],
        };
      });

      setChats((prev) =>
        (Array.isArray(prev) ? prev : []).map((chat) =>
          chat.id === chatId ? { ...chat, chat_type: "standard", review_phase: "consultation" } : chat
        )
      );

      return data;
    } catch (error) {
      console.error("Error ending review chat:", error);
      return {
        error: {
          code: "network_error",
          message: "Network error. Please try again.",
        },
      };
    } finally {
      setSending(false);
    }
  }, []);

  // Toggle archive status
  const toggleArchive = useCallback(async (chatId, isArchived = true) => {
    if (!chatId) return;
    
    try {
      const response = await apiFetch(`/api/ai/chats/${chatId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_archived: isArchived }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setChats(prev => 
          (Array.isArray(prev) ? prev : []).map(chat =>
            chat.id === chatId ? { ...chat, is_archived: data.is_archived } : chat
          )
        );
        setCurrentChat(prev => {
          if (!prev || prev.id !== chatId) return prev;
          return { ...prev, is_archived: data.is_archived };
        });
      } else {
        console.error("Failed to toggle archive");
      }
    } catch (error) {
      console.error("Error toggling archive:", error);
    }
  }, []);

  // Update chat-scoped pinned AI input assets (Option B).
  // Set semantics: pinnedAssetIds is the full list to persist (max 5 enforced by backend).
  const updatePinnedAssets = useCallback(async (chatId, pinnedAssetIds) => {
    if (!chatId) return null;
    const ids = Array.isArray(pinnedAssetIds) ? pinnedAssetIds : [];
    try {
      const response = await apiFetch(`/api/ai/chats/${chatId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned_asset_ids: ids }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        return { error: data?.error || "Failed to update pinned assets", code: data?.code };
      }

      // Keep local chat metadata in sync, then refresh the chat detail to get pinned asset objects.
      setChats(prev =>
        (Array.isArray(prev) ? prev : []).map(chat =>
          chat.id === chatId ? { ...chat, updated_at: data?.updated_at || chat.updated_at } : chat
        )
      );
      await fetchChat(chatId, true);
      return data;
    } catch (error) {
      console.error("Error updating pinned assets:", error);
      return { error: "Network error updating pinned assets", code: "network_error" };
    }
  }, [fetchChat]);

  // Fetch usage statistics
  const fetchUsage = useCallback(async () => {
    try {
      const response = await apiFetch(`/api/ai/usage/`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      } else {
        console.error("Failed to fetch usage");
      }
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  }, []);

  // Undo an operation
  const undoOperation = useCallback(async (operationId) => {
    if (!operationId) return null;
    
    try {
      const response = await apiFetch(`/api/ai/operations/${operationId}/undo/`, {
        method: "POST",
      });
      
      if (response.ok) {
        const data = await response.json();

        // Mark all undone operations in local chat state so the UI reflects "undone" immediately.
        const undoneSet = new Set(
          Array.isArray(data.undone_operations) ? data.undone_operations : [operationId]
        );
        setCurrentChat((prev) => {
          if (!prev) return prev;
          const nextMessages = (prev.messages || []).map((m) => {
            if (m.role !== "assistant" || !Array.isArray(m.operations)) return m;
            const nextOps = m.operations.map((op) =>
              undoneSet.has(op.id)
                ? { ...op, execution_status: "undone", is_undone: true }
                : op
            );
            return { ...m, operations: nextOps };
          });
          return { ...prev, messages: nextMessages };
        });

        // Refresh affected elements so the rendered website updates without a page reload.
        if (Array.isArray(data.reload_targets)) {
          const dedupedTargets = dedupeReloadTargets(data.reload_targets);
          for (const target of dedupedTargets) {
            try {
              await refreshElement(target.element_id, target.element_type);
            } catch (e) {
              console.warn("[AIContext] refreshElement failed (undo)", e);
            }
          }
        }

        return data;
      } else {
        console.error("Failed to undo operation");
        return null;
      }
    } catch (error) {
      console.error("Error undoing operation:", error);
      return null;
    }
  }, [refreshElement]);

  // Get cascade preview
  const getCascadePreview = useCallback(async (operationId) => {
    if (!operationId) return null;
    
    try {
      const response = await apiFetch(`/api/ai/operations/${operationId}/cascade-preview/`);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error("Failed to get cascade preview");
        return null;
      }
    } catch (error) {
      console.error("Error getting cascade preview:", error);
      return null;
    }
  }, []);

  // Polling logic
  useEffect(() => {
    if (hasActiveOperations && currentChatId) {
      // Start polling
      if (!pollingIntervalRef.current) {
        pollStartTimeRef.current = Date.now();
        pollingIntervalRef.current = setInterval(() => {
          void (async () => {
            const pollStartTime = pollStartTimeRef.current;
            if (pollStartTime && (Date.now() - pollStartTime > OPERATION_TIMEOUT_MS)) {
              // Timeout exceeded
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
              pollStartTimeRef.current = null;

              // Update local state to show timeout error on operations
              setCurrentChat((prev) => {
                if (!prev) return prev;
                const updated = JSON.parse(JSON.stringify(prev));
                updated.messages.forEach((msg) => {
                  if (msg.role !== "assistant") return;
                  const ops = msg.operations || [];
                  ops.forEach((op) => {
                    if (op.execution_status === "pending" || op.execution_status === "executing") {
                      op.execution_status = "failed";
                      op.error_message = "Operation timed out. Please try again.";
                    }
                  });
                });
                return updated;
              });
              return;
            }

            tickCountRef.current += 1;

            const chatSnapshot = currentChatRef.current;
            const assistantMessageIds = getAssistantMessageIdsWithActiveOperations(chatSnapshot);

            const pendingReloadTargets = [];

            for (const messageId of assistantMessageIds) {
              const statusPayload = await checkOperationStatus(messageId);
              const ops = statusPayload?.operations;
              if (!Array.isArray(ops)) continue;

              // Merge operation statuses into the message in currentChat
              setCurrentChat((prev) => {
                if (!prev) return prev;
                const nextMessages = (prev.messages || []).map((m) => {
                  if (m.id !== messageId) return m;
                  return { ...m, operations: ops };
                });
                return { ...prev, messages: nextMessages };
              });

              // Completion transitions -> incremental reload targets
              const completedSet =
                completedOpsByMessageIdRef.current.get(messageId) || new Set();
              const { newlyCompletedOperationIds, reloadTargets } =
                getNewlyCompletedOperationReloadTargets(ops, completedSet);

              if (newlyCompletedOperationIds.size > 0) {
                newlyCompletedOperationIds.forEach((id) => completedSet.add(id));
                completedOpsByMessageIdRef.current.set(messageId, completedSet);
                pendingReloadTargets.push(...reloadTargets);
              }
            }

            const dedupedTargets = dedupeReloadTargets(pendingReloadTargets);
            for (const target of dedupedTargets) {
              try {
                await refreshElement(target.element_id, target.element_type);
              } catch (e) {
                console.warn("[AIContext] refreshElement failed", e);
              }
            }

            // Optional: sync chat payload less frequently (do not rely on it for operation status)
            if (tickCountRef.current % 5 === 0) {
              fetchChat(currentChatId, true);
            }
          })();
        }, 2000);
      }
    } else {
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        pollStartTimeRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [hasActiveOperations, currentChatId, fetchChat, checkOperationStatus, refreshElement]);

  const value = useMemo(() => ({
    chats,
    currentChatId,
    currentChat,
    loading,
    sending,
    usage,
    fetchChats,
    fetchChat,
    createChat,
    updateChat,
    setCurrentChatId,
    sendMessage,
    sendReviewAction,
    startReviewChat,
    endReviewChat,
    toggleArchive,
    updatePinnedAssets,
    undoOperation,
    getCascadePreview,
    checkOperationStatus,
    fetchUsage,
  }), [
    chats,
    currentChatId,
    currentChat,
    loading,
    sending,
    usage,
    fetchChats,
    fetchChat,
    createChat,
    updateChat,
    sendMessage,
    sendReviewAction,
    startReviewChat,
    endReviewChat,
    toggleArchive,
    updatePinnedAssets,
    undoOperation,
    getCascadePreview,
    checkOperationStatus,
    fetchUsage,
  ]);

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};
